/**
 * "Your render is done" — the toast that catches the ready moment from
 * anywhere in the app.
 *
 * A render takes around ten minutes. Nobody watches the job screen for that long,
 * so the moment it finishes has to find the user wherever they are: the app
 * shell polls the jobs THIS browser started (`lib/trackedJobs.ts`) and pushes
 * one of these when a tracked job reaches a terminal status.
 *
 * Bottom-right and above the Studio's "Design agent" FAB (which sits at
 * `bottom: 20 / z-index: 60`), so a toast can never swallow the button that
 * the toast itself is inviting you to use.
 */
import { useEffect } from "react";
import { CheckCircle2, Clapperboard, TriangleAlert, X } from "lucide-react";

export interface RenderToast {
  /** The job this toast is about — also its React key and dismiss handle. */
  jobId: string;
  kind: "ready" | "failed";
  /** Clip title, or the job id when the server has no nicer name. */
  title: string;
  /** Failure detail. Only read for `kind: "failed"`. */
  message?: string;
}

/** How long a toast stays before it dismisses itself. Long enough to notice
 *  after switching back to the tab, short enough not to camp on the UI. */
export const TOAST_TTL_MS = 20_000;

/** At most this many on screen at once — beyond that they stop being a
 *  notification and become a wall. The host passes them NEWEST FIRST; the
 *  extras are dropped from the bottom, not silently lost (the jobs are still
 *  in the Queue). */
export const MAX_VISIBLE_TOASTS = 3;

interface Props {
  /** Newest first. */
  toasts: RenderToast[];
  onOpenStudio: (jobId: string) => void;
  onOpenJob: (jobId: string) => void;
  onDismiss: (jobId: string) => void;
}

export function RenderReadyToast({ toasts, onOpenStudio, onOpenJob, onDismiss }: Props): React.JSX.Element | null {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        // Clears the Studio FAB (44px tall at bottom: 20) so both stay usable.
        bottom: 76,
        left: "auto",
        zIndex: 70,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxWidth: "min(360px, calc(100vw - 32px))",
        pointerEvents: "none",
      }}
    >
      {toasts.slice(0, MAX_VISIBLE_TOASTS).map((t) => (
        <ToastCard
          key={t.jobId}
          toast={t}
          onOpenStudio={onOpenStudio}
          onOpenJob={onOpenJob}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}

function ToastCard({
  toast,
  onOpenStudio,
  onOpenJob,
  onDismiss,
}: { toast: RenderToast } & Omit<Props, "toasts">): React.JSX.Element {
  const { jobId, kind, title, message } = toast;

  // Self-dismiss. Keyed by jobId at the parent, so a re-render never restarts
  // a running timer and a replaced toast gets a fresh one.
  useEffect(() => {
    const t = window.setTimeout(() => onDismiss(jobId), TOAST_TTL_MS);
    return () => window.clearTimeout(t);
  }, [jobId, onDismiss]);

  const ok = kind === "ready";
  return (
    <div
      role={ok ? "status" : "alert"}
      className="ql-fade-up"
      style={{
        pointerEvents: "auto",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 16,
        background: "rgba(16,18,27,.97)",
        border: `1px solid ${ok ? "rgba(74,222,128,.30)" : "rgba(248,113,113,.32)"}`,
        boxShadow: "0 10px 34px rgba(0,0,0,.55)",
        backdropFilter: "blur(10px)",
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 1, color: ok ? "var(--ql-success)" : "var(--ql-danger)" }}>
        {ok ? <CheckCircle2 size={17} strokeWidth={2} /> : <TriangleAlert size={17} strokeWidth={2} />}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>
          {ok ? `${title} is ready` : `${title} failed`}
        </div>
        {!ok && message && (
          <div
            style={{
              fontSize: 11,
              color: "var(--ql-fg-muted)",
              marginTop: 3,
              overflowWrap: "anywhere",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {message}
          </div>
        )}
        <button
          type="button"
          onClick={() => { onDismiss(jobId); (ok ? onOpenStudio : onOpenJob)(jobId); }}
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "6px 12px",
            borderRadius: 999,
            border: `1px solid ${ok ? "rgba(255,213,0,.40)" : "rgba(255,255,255,.14)"}`,
            background: ok ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.05)",
            color: ok ? "var(--ql-yellow)" : "var(--ql-fg)",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {ok ? <><Clapperboard size={13} strokeWidth={2} /> Open in Studio</> : "Open job"}
        </button>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(jobId)}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: 8,
          border: "none",
          background: "transparent",
          color: "var(--ql-fg-subtle)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
